import usePageTitle from "../utils/usePageTitle";
import { Route, Users } from "lucide-react";
import { useAskSidebar, usePageAsk } from "../chat/AskContext";
import { filtersForScope } from "../chat/askScope";
import { kpiAskEvidence } from "../chat/kpiAskEvidence";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import FilterMultiSelect, { type FilterOption } from "../components/FilterMultiSelect";
import UserFilterMultiSelect from "../components/UserFilterMultiSelect";
import AlertDeleteDialog from "../components/watchlist/AlertDeleteDialog";
import AlertDetail from "../components/watchlist/AlertDetail";
import { getBrowserHash, splitHash, updateHash } from "../utils/urlHash";
import AlertList from "../components/watchlist/AlertList";
import type { AlertHandlers } from "../components/watchlist/alertActions";
import CreateMenu from "../components/watchlist/CreateMenu";
import RecentQuestions from "../components/watchlist/RecentQuestions";
import { useLogin } from "../reducers";
import { scopeOverlaps } from "../watchlist/alertFilters";
import {
  delete_alert,
  mark_alert_seen,
  refresh_alerts,
  run_alert,
  update_alert,
} from "../watchlist/api";
import { useAlerts } from "../watchlist/hooks";
import type { Alert } from "../watchlist/types";
import { useWatchContext } from "../watchlist/useWatchFlow";

/**
 * The Insights page: every saved alert and KPI with its latest result, and the
 * one-off analyses asked this session. Asking and KPI setup both happen in the
 * shared Ask sidebar; this page starts them and lists what they left behind.
 */
export const WATCHLIST_SEARCH_HASH_KEY = "watchSearch";
export const WATCHLIST_STATUS_HASH_KEY = "watchStatus";
export const WATCHLIST_PEOPLE_HASH_KEY = "watchPeople";
export const WATCHLIST_PROCESSES_HASH_KEY = "watchProcesses";

/** Absent means unfiltered; present, even empty, is exactly the listed ids. */
const readSelection = (value: string | null): string[] | null =>
  value === null ? null : value.split(",").filter(Boolean);

const writeSelection = (ids: string[], options: FilterOption[]): string | null =>
  options.every(option => ids.includes(option.id)) ? null : ids.join(",");

const selectionLabel = (ids: string[] | null, options: FilterOption[], allLabel: string) =>
  ids === null
    ? allLabel
    : options
        .filter(option => ids.includes(option.id))
        .map(option => option.label)
        .join(", ");

const Watchlist = () => {
  const { t } = useTranslation(undefined, { keyPrefix: "watchlist" });
  const { t: tRoot } = useTranslation();

  // The open alert lives in the URL (/watchlist/<alert id>), so a detail view
  // can be linked, reloaded, and left with the browser's own back button.
  const { alertId: openAlertId } = useParams<{ alertId?: string }>();
  const navigate = useNavigate();
  // How the page was reached lives in route state rather than the URL: it is
  // how you arrived, not where you are, and a reloaded or shared link should
  // land on the page as it stands. `edit` opens an alert detail in edit mode;
  // `watchProcessSlug` is the "Watch this process" jump from a process detail.
  const location = useLocation();
  const params = splitHash(location.hash).params;
  const status = params.get(WATCHLIST_STATUS_HASH_KEY);
  const filter = status === "active" || status === "paused" ? status : "all";
  const query = params.get(WATCHLIST_SEARCH_HASH_KEY) ?? "";
  const peopleFilter = readSelection(params.get(WATCHLIST_PEOPLE_HASH_KEY));
  const processFilter = readSelection(params.get(WATCHLIST_PROCESSES_HASH_KEY));
  const watchlistRoute = (pathname: string) => ({
    pathname,
    search: location.search,
    hash: getBrowserHash(),
  });
  const changeFilter = (updates: Parameters<typeof updateHash>[1]) =>
    navigate(
      { ...watchlistRoute(location.pathname), hash: updateHash(getBrowserHash(), updates) },
      { replace: true, state: location.state },
    );
  const routeState = location.state as { edit?: boolean; watchProcessSlug?: string } | null;
  const startInEdit = routeState?.edit === true;
  const watchProcessSlug = routeState?.watchProcessSlug ?? null;

  const ask = useAskSidebar();
  const { permissions } = useLogin();
  // Someone who only sees their own work gets the page about their own work:
  // no people filter, and copy that speaks to them rather than a team.
  const personal = permissions.recordings.visualization === "OnlyMine";
  const { userOptions, processOptions } = useWatchContext();
  const showPeople = !personal && userOptions.length > 1;
  const alerts = useAlerts();
  const scopedAlerts = alerts.filter(alert =>
    scopeOverlaps(alert.scope, { userIds: peopleFilter, processSlugs: processFilter }),
  );
  const [askAnchor] = useState(() => Date.now());
  const askScope = {
    userIds: peopleFilter ?? userOptions.map(user => user.id),
    processSlugs: processFilter ?? [],
    window: { days: 30 },
  };
  usePageAsk({
    sessionKey: "kpis:list",
    page: { kind: "kpis" },
    label: [
      tRoot("askPanel.page_kpis"),
      ...(showPeople ? [selectionLabel(peopleFilter, userOptions, t("all_people"))] : []),
      selectionLabel(processFilter, processOptions, t("all_processes")),
    ].join(" · "),
    filters: filtersForScope(askScope, processOptions, askAnchor),
    suggestions: [],
    context: `${tRoot("askPanel.kpi_context")}\n\n${kpiAskEvidence(scopedAlerts)}`,
    empty: askScope.userIds.length === 0 || processFilter?.length === 0,
  });
  const [alertToDelete, setAlertToDelete] = useState<Alert | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    // A failure leaves whatever alerts the store last loaded.
    void refresh_alerts().catch(() => undefined);
  }, []);

  useEffect(() => {
    if (watchProcessSlug) ask.openWatch({ processSlug: watchProcessSlug });
  }, [watchProcessSlug, ask.openWatch]);

  const openDetail = (alert: Alert) => navigate(watchlistRoute(`/watchlist/${alert.id}/`));

  const handlersFor = (alert: Alert): AlertHandlers => ({
    // Runs regardless of whether the alert is paused: asking for it is the
    // point, and a paused one still has a scope worth evaluating. Progress is
    // reflected in the row immediately, so the list stays in place while it
    // runs; invoking the same action from detail naturally stays there too.
    onRunNow: () => run_alert(alert.id),
    onToggleEnabled: () =>
      void update_alert(alert.id, { enabled: !alert.enabled }).catch(() => undefined),
    onRename: title => update_alert(alert.id, { title }),
    // Editing belongs to the alert, not to the composer the alert was born in.
    onEdit: () => navigate(watchlistRoute(`/watchlist/${alert.id}/`), { state: { edit: true } }),
    onDelete: () => {
      setDeleteError(null);
      setAlertToDelete(alert);
    },
  });

  const openAlert = alerts.find(alert => alert.id === openAlertId) ?? null;
  usePageTitle(openAlert?.title.trim() || t("title"));

  const cancelDelete = () => {
    if (deleteBusy) return;
    setAlertToDelete(null);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!alertToDelete || deleteBusy) return;
    const alertId = alertToDelete.id;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await delete_alert(alertId);
      setAlertToDelete(null);
      if (openAlertId === alertId) navigate(watchlistRoute("/watchlist/"));
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : t("delete_alert_failed"));
    } finally {
      setDeleteBusy(false);
    }
  };

  const deleteDialog = (
    <AlertDeleteDialog
      alert={alertToDelete}
      busy={deleteBusy}
      error={deleteError}
      onCancel={cancelDelete}
      onConfirm={confirmDelete}
    />
  );

  // Being on the detail is the acknowledgement, however it was reached —
  // row click, run-now, or a direct link: the row's badge and the sidebar
  // navigation dot go out together.
  useEffect(() => {
    if (openAlert?.unread) void mark_alert_seen(openAlert.id).catch(() => undefined);
  }, [openAlert?.id, openAlert?.unread]);

  // An open alert takes over the route rather than sitting in a dialog over it:
  // it owns the window height, and the chat lives at the bottom of that.
  if (openAlert)
    return (
      <>
        <AlertDetail
          alert={openAlert}
          onBack={() => navigate(watchlistRoute("/watchlist/"))}
          handlers={handlersFor(openAlert)}
          userOptions={userOptions}
          processOptions={processOptions}
          startInEdit={startInEdit}
        />
        {deleteDialog}
      </>
    );

  return (
    <div className="dashboard-page">
      <div className="dashboard-page-header mb-8">
        <div className="min-w-64 flex-1">
          <h1 className="dashboard-page-title">{t("title")}</h1>
          <p className="dashboard-page-description">
            {t(personal ? "subtitle_personal" : "subtitle_team")}
          </p>
        </div>
        <div className="ml-auto flex max-w-full flex-wrap items-start justify-end gap-3">
          {showPeople && (
            <div className="dashboard-people-filter min-w-40">
              <UserFilterMultiSelect
                allLabel={t("all_people")}
                icon={<Users aria-hidden="true" className="h-4 w-4 shrink-0 text-text-dim" />}
                value={peopleFilter ?? userOptions.map(user => user.id)}
                options={userOptions}
                onChange={ids =>
                  changeFilter({ [WATCHLIST_PEOPLE_HASH_KEY]: writeSelection(ids, userOptions) })
                }
              />
            </div>
          )}
          {processOptions.length > 0 && (
            <div className="dashboard-people-filter min-w-40">
              <FilterMultiSelect
                allLabel={t("all_processes")}
                selectAllLabel={t("all_processes")}
                emptyLabel={t("select_processes")}
                searchPlaceholder={tRoot("search_in_processes")}
                multipleLabel={count => t("scope_processes", { count })}
                icon={<Route aria-hidden="true" className="h-4 w-4 shrink-0 text-text-dim" />}
                value={processFilter ?? processOptions.map(process => process.id)}
                options={processOptions}
                onChange={ids =>
                  changeFilter({
                    [WATCHLIST_PROCESSES_HASH_KEY]: writeSelection(ids, processOptions),
                  })
                }
              />
            </div>
          )}
          <CreateMenu
            onTrackKpi={() => ask.openWatch({ context: null })}
            onRunAnalysis={() => ask.openQuestion({})}
          />
        </div>
      </div>

      <AlertList
        alerts={scopedAlerts}
        totalCount={alerts.length}
        onOpen={openDetail}
        handlersFor={handlersFor}
        filter={filter}
        query={query}
        onFilterChange={value =>
          changeFilter({ [WATCHLIST_STATUS_HASH_KEY]: value === "all" ? null : value })
        }
        onQueryChange={value => changeFilter({ [WATCHLIST_SEARCH_HASH_KEY]: value || null })}
      />

      {/* Kept for this browser session only, and only the latest few: a way
          back into recent questions, not an archive of them. */}
      <section aria-labelledby="recent-analyses-heading" className="mt-10">
        <h2 id="recent-analyses-heading" className="mb-4 text-base font-semibold text-text">
          {t("recent_analyses")}
        </h2>
        <div className="rounded-2xl border border-outline/70 bg-surface px-5 py-3">
          <RecentQuestions
            emptyLabel={t("recent_analyses_empty")}
            onOpen={question => ask.openQuestion({ ...question, questionId: question.id })}
          />
        </div>
      </section>

      {deleteDialog}
    </div>
  );
};

export default Watchlist;
